import { createCanvas, destroyCanvas } from "@/libs/canvas"
import type { FramePixels } from "@/libs/cv/modules/types"
import { WebGLContextLostError } from "@/libs/gl/error"
import { fit, getImageSourceDimensions, isVideoFrame, type ImageSource } from "@/libs/image"

export class PixelsReader {
  #targetResolution: number

  #gl: WebGL2RenderingContext | null
  #canvas: HTMLCanvasElement | OffscreenCanvas | null

  /** Cached GPU resources for source image */
  #source: { framebuffer: WebGLFramebuffer; attachment: WebGLTexture } | undefined
  /** Cached GPU resources for resized source image */
  #resized: { framebuffer: WebGLFramebuffer; attachment: WebGLRenderbuffer } | undefined

  #isContextLost = false

  constructor(targetResolution = 480) {
    this.#canvas = createCanvas(1, 1)
    this.#canvas.addEventListener("webglcontextlost", this.#handleContextLost, { once: true })

    this.#gl = this.#canvas.getContext("webgl2", {
      willReadFrequently: true,
    }) as WebGL2RenderingContext
    this.#targetResolution = targetResolution
  }

  read(imageSource: ImageSource): FramePixels {
    if (!this.#gl) throw new Error("WebGL context is not available")

    const dims = getImageSourceDimensions(imageSource)
    const { width, height } = fit(
      { width: dims.width, height: dims.height },
      this.#targetResolution,
    )
    const pixels = new Uint8Array(width * height * 4)

    try {
      this.#getPixels(imageSource, pixels, { resizeHeight: height, resizeWidth: width })
    } catch (e) {
      throw this.#isContextLost
        ? new WebGLContextLostError("WebGL context lost while reading pixels", { cause: e })
        : new Error("Failed to read pixels", { cause: e })
    }

    return { width, height, format: "RGBA", data: pixels }
  }

  destroy() {
    if (this.#gl && !this.#isContextLost) {
      const gl = this.#gl

      if (this.#source) {
        gl.deleteTexture(this.#source.attachment)
        gl.deleteFramebuffer(this.#source.framebuffer)
        this.#source = undefined
      }
      if (this.#resized) {
        gl.deleteRenderbuffer(this.#resized.attachment)
        gl.deleteFramebuffer(this.#resized.framebuffer)
        this.#resized = undefined
      }

      const loseContext = gl.getExtension("WEBGL_lose_context")
      // Safari enforces a tight cap on active WebGL contexts; without an explicit reset, repeated Face180 sessions crash the tab.
      loseContext?.loseContext()
      console.warn("PixelsReader: WebGL context destroyed")
    }

    this.#canvas?.removeEventListener("webglcontextlost", this.#handleContextLost)
    if (this.#canvas) destroyCanvas(this.#canvas)

    this.#gl = null
    this.#canvas = null
  }

  #getPixels(
    image: ImageSource,
    destination: ArrayBufferView,
    options?: {
      resizeWidth?: number
      resizeHeight?: number
    },
  ) {
    const gl = this.#gl
    if (!gl) throw new Error("WebGL context is not available")

    if (this.#isContextLost)
      throw new WebGLContextLostError("WebGL context is lost, unable to read pixels")

    const { width: sourceWidth, height: sourceHeight } = getImageSourceDimensions(image)

    const width = options?.resizeWidth ?? sourceWidth
    const height = options?.resizeHeight ?? sourceHeight
    const format = "RGBA"

    // TODO: Handle `texture` and `framebuffer` recreation on `image` dimensions change
    // One possible scenario of this is a runtime change of the camera stream resolution, e.g. if LIQA were switching from low-res to hi-res video
    // But as of now there is no such use-case and is unlikely to appear
    if (!this.#source) {
      const attachment = gl.createTexture()!
      gl.bindTexture(gl.TEXTURE_2D, attachment)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl[format], gl[format], gl.UNSIGNED_BYTE, image)
      gl.bindTexture(gl.TEXTURE_2D, null)

      const framebuffer = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, attachment, 0)
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)

      this.#source = { framebuffer, attachment }
    }

    gl.bindTexture(gl.TEXTURE_2D, this.#source.attachment)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl[format], gl[format], gl.UNSIGNED_BYTE, image)
    gl.bindTexture(gl.TEXTURE_2D, null)

    // TODO: Handle `renderbuffer` and `framebuffer` recreation on `options.resizeWidth` and `options.resizeHeight` change
    // There is no such use-case now and is unlikely to appear
    if (!this.#resized && (width !== sourceWidth || height !== sourceHeight)) {
      const attachment = gl.createRenderbuffer()!
      gl.bindRenderbuffer(gl.RENDERBUFFER, attachment)
      gl.renderbufferStorage(gl.RENDERBUFFER, gl[`${format}8`], width, height)
      gl.bindRenderbuffer(gl.RENDERBUFFER, null)

      const framebuffer = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, attachment)
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)

      this.#resized = { framebuffer, attachment }
    }

    const [sw, sh] = isVideoFrame(image)
      ? [image.visibleRect!.width, image.visibleRect!.height]
      : [sourceWidth, sourceHeight]

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.#source.framebuffer)

    if (this.#resized)
      // transfer `source` texture to `resized` renderbuffer thus performing resize
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.#resized.framebuffer)

    gl.blitFramebuffer(0, 0, sw, sh, 0, 0, width, height, gl.COLOR_BUFFER_BIT, gl.NEAREST)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)

    if (this.#resized)
      // use the resized framebuffer as the source for readPixels if a resize operation has taken place
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.#resized.framebuffer)

    gl.finish()
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, destination)
  }

  #handleContextLost = (event: Event) => {
    event.preventDefault()
    this.#isContextLost = true
  }
}
